// ============================================================================
// Department Report — polished report renderer (ISOLATED MODULE)
// Renders the structured report (headline + sections + per-member highlights)
// as a clean, report-style document. Falls back to MessageRenderer for raw
// text if structured JSON wasn't available.
// ============================================================================

import React from 'react';
import { Building2, CheckCircle2, Layers, AlertTriangle, Clock, Users, TrendingUp, Inbox } from 'lucide-react';
import MessageRenderer from '../../../components/MessageRenderer/MessageRenderer';

// Pick an icon per known section title.
const sectionIcon = (title = '') => {
  const t = title.toLowerCase();
  if (t.includes('win')) return <CheckCircle2 className="h-4 w-4" />;
  if (t.includes('theme')) return <Layers className="h-4 w-4" />;
  if (t.includes('block') || t.includes('issue')) return <AlertTriangle className="h-4 w-4" />;
  return <TrendingUp className="h-4 w-4" />;
};

const ReportView = ({ report, isDark }) => {
  const card = {
    border: `1px solid ${isDark ? 'rgba(148,163,184,0.18)' : 'rgba(148,163,184,0.25)'}`,
    borderRadius: 14,
    background: isDark ? 'rgba(30,30,30,0.5)' : '#ffffff',
    padding: 18,
    marginBottom: 16,
  };

  const Stat = ({ icon, label, value, accent }) => (
    <div
      style={{
        flex: '1 1 120px', minWidth: 120, padding: '12px 14px', borderRadius: 12,
        background: isDark ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.06)',
        border: '1px solid rgba(99,102,241,0.18)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, opacity: 0.7, marginBottom: 4 }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent }}>{value}</div>
    </div>
  );

  const hasStructured = Array.isArray(report.sections) && report.sections.length > 0;
  const isEmpty =
    report.empty === true ||
    (report.stats?.withData === 0 && !hasStructured && !report.headline);

  return (
    <div
      style={{
        marginTop: 20, borderRadius: 16, overflow: 'hidden',
        border: `1px solid ${isDark ? 'rgba(148,163,184,0.2)' : 'rgba(148,163,184,0.25)'}`,
        background: isDark ? 'rgba(20,20,22,0.6)' : '#f8fafc',
      }}
    >
      {/* Cover / title band */}
      <div
        style={{
          padding: '22px 24px',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: '#fff',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 0.92, fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>
          <Building2 className="h-4 w-4" /> DEPARTMENT DAILY REPORT
          {report.cached && (
            <span style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.22)', fontSize: 10, letterSpacing: 0.3 }}>
              CACHED
            </span>
          )}
        </div>
        <h1 style={{ margin: '6px 0 2px', fontSize: 26, fontWeight: 800 }}>{report.department}</h1>
        <div style={{ fontSize: 13, opacity: 0.9 }}>
          {report.startDate === report.endDate ? report.startDate : `${report.startDate} → ${report.endDate}`}
        </div>
        {report.headline && (
          <p style={{ margin: '12px 0 0', fontSize: 15, lineHeight: 1.5, opacity: 0.96, maxWidth: 760 }}>
            {report.headline}
          </p>
        )}
      </div>

      <div style={{ padding: 24 }}>
        {/* Stat chips */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          <Stat icon={<Users className="h-3.5 w-3.5" />} label="Members" value={report.stats?.memberCount ?? 0} accent="#6366f1" />
          <Stat icon={<TrendingUp className="h-3.5 w-3.5" />} label="Active" value={report.stats?.withData ?? 0} accent="#10b981" />
          {report.stats?.totalHours != null && (
            <Stat icon={<Clock className="h-3.5 w-3.5" />} label="Total Hours" value={`${report.stats.totalHours}h`} accent="#f59e0b" />
          )}
        </div>

        {isEmpty ? (
          <div
            style={{
              ...card,
              textAlign: 'center',
              padding: '36px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isDark ? 'rgba(148,163,184,0.12)' : 'rgba(148,163,184,0.14)',
                color: '#94a3b8',
              }}
            >
              <Inbox className="h-5 w-5" />
            </span>
            <div style={{ fontSize: 16, fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
              No activity for this period
            </div>
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, opacity: 0.75, maxWidth: 480 }}>
              {report.overview ||
                'Neither desktop app sessions nor stealth agent monitoring had data for the selected members and dates.'}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12.5, opacity: 0.55, maxWidth: 440 }}>
              Pick a day when the team was working, or confirm stealth devices were online and reporting.
            </p>
          </div>
        ) : hasStructured ? (
          <>
            {report.sections.map((s, i) => (
              <div key={i} style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: '#6366f1' }}>
                  {sectionIcon(s.title)}
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    {s.title}
                  </h3>
                </div>
                {s.body && (
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, opacity: 0.9 }}>{s.body}</p>
                )}
                {Array.isArray(s.bullets) && s.bullets.length > 0 && (
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {s.bullets.map((b, bi) => (
                      <li key={bi} style={{ fontSize: 14, lineHeight: 1.7, opacity: 0.9 }}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {/* Per-member highlights as a clean table-like list */}
            {Array.isArray(report.memberHighlights) && report.memberHighlights.length > 0 && (
              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: '#6366f1' }}>
                  <Users className="h-4 w-4" />
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    Per-Member Highlights
                  </h3>
                </div>
                <div>
                  {report.memberHighlights.map((m, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex', gap: 12, padding: '10px 0',
                        borderTop: i === 0 ? 'none' : `1px solid ${isDark ? 'rgba(148,163,184,0.12)' : 'rgba(148,163,184,0.18)'}`,
                      }}
                    >
                      <div style={{ flex: '0 0 auto', minWidth: 150 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600 }}>{m.name}</div>
                        {m.hours != null && (
                          <div style={{ fontSize: 12, opacity: 0.6 }}>{Number(m.hours).toFixed(2)}h</div>
                        )}
                      </div>
                      <div style={{ flex: 1, fontSize: 13.5, lineHeight: 1.55, opacity: 0.9 }}>{m.highlight}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          // Fallback: structured JSON wasn't available — render raw text nicely.
          <div style={card}>
            <MessageRenderer text={report.overview} isDark={isDark} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportView;
